import { Button } from "@/components/ui/baseUi/button"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { errorToastClassName } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { loginFieldsSchema, type LoginFields } from "@/modules/auth/authTypes"
import { useLogin } from "@/modules/auth/hooks/useLogin"
import { getErrorCodeFromPayload } from "@/modules/auth/utils/getErrorCodeFromPayload"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { getAuthError } from "@modules/auth/authError"
import { AuthError, LoginNextStep, LoginNextStepError, UserErrorCodes } from "@modules/auth/authErrorTypes"
import { AuthContent, AuthForm, AuthHeader, AuthPage, AuthSubmitButton } from "@modules/auth/components/authComponents"
import { LoggerService } from "@modules/common/logger/loggerService"
import { word } from "@modules/core/utils/i18n"
import { Path } from "@modules/navigation/routes"
import { LoggerType } from "@modules/utils/loggerUtils"
import { AuthError as AmplifyAuthError, resetPassword, signOut } from "aws-amplify/auth"
import { AxiosError } from "axios"
import { useEffect } from "react"
import { Link, useLocation, useNavigate } from "react-router"
import { toast } from "sonner"

const defaultValues: LoginFields = {
  email: "",
  password: "",
}

export function LoginPage() {
  const location = useLocation() as { state?: { email?: string } }
  const navigate = useNavigate()
  const login = useLogin()
  const language = useLanguage()
  useEffect(() => {
    void signOut()
  }, [])

  const navigateToConfirmEmail = async () => {
    await navigate({ pathname: Path.ConfirmEmail }, { state: { email: form.state.values.email } })
  }

  const startPasswordReset = async () => {
    await sendForgotPasswordConfirmationCode()
    await navigate(
      { pathname: Path.ResetPassword },
      { state: { email: form.state.values.email, isRequiredPassword: true } },
    )
  }

  const loginSubmit = async () => {
    try {
      await login(form.state.values)
    } catch (e) {
      console.error(LoggerType.Login + "login", e)

      if (e instanceof AxiosError) {
        const errorCode = getErrorCodeFromPayload(e.response?.data)
        if (e.status === 404 && errorCode === UserErrorCodes.UserNotExistsCode) {
          await navigate(Path.Register)
          return
        }
      }

      // Next sign-in step required, raised by useLogin with a typed step (not a stringly-typed message).
      if (e instanceof LoginNextStepError) {
        switch (e.step) {
          case LoginNextStep.ConfirmSignUp:
            await navigateToConfirmEmail()
            return
          case LoginNextStep.ResetPassword:
            await startPasswordReset()
            return
        }
      }

      if (e instanceof AmplifyAuthError) {
        // Already authenticated: just go back to the login flow, no error toast needed.
        if (e.name === AuthError.UserAlreadyAuthenticatedException) {
          await navigate(Path.Login)
          return
        }
        // Cognito can surface these directly (not via a nextStep) — route them the same way.
        if (e.name === AuthError.UserNotConfirmedException) {
          await navigateToConfirmEmail()
          return
        }
        if (e.name === AuthError.PasswordResetRequiredException) {
          await startPasswordReset()
          return
        }
        toast.error(getAuthError(e), { classNames: errorToastClassName() })
        return
      }

      // Any other failure (network, server, unexpected) — capture for observability and always give
      // the user feedback instead of silently doing nothing.
      LoggerService.captureException(LoggerType.Login + "unexpected login failure", e)
      toast.error(word("global.error.default"), { classNames: errorToastClassName() })
    }
  }

  const sendForgotPasswordConfirmationCode = async () => {
    try {
      await resetPassword({ username: form.state.values.email })
    } catch (e) {
      console.error(LoggerType.Login + "send confimation code password", e)
      if (e instanceof AmplifyAuthError) {
        toast.error(getAuthError(e), { classNames: errorToastClassName() })
      } else {
        toast.error(word("global.error.default"), { classNames: errorToastClassName() })
      }
    }
  }

  const navigateToSendCode = async () => {
    await navigate(Path.SendCode, { state: { email: form.state.values.email } })
  }

  const form = useAppForm({
    defaultValues: {
      ...defaultValues,
      email: location.state?.email ?? "",
    },
    validators: {
      onChange: loginFieldsSchema(language),
      onSubmit: loginFieldsSchema(language),
      onBlur: loginFieldsSchema(language),
    },
    onSubmit: loginSubmit,
  })

  return (
    <AuthPage>
      <AuthForm
        onSubmit={(e) => {
          e.preventDefault()
          void form.handleSubmit()
        }}
      >
        <AuthHeader title={word("auth.welcomeBack")} subtitle={word("auth.login.form.subtitle")} />
        <AuthContent style={{ gap: 10 }}>
          <div className="flex flex-col gap-2">
            <form.AppField name="email">
              {(field) => (
                <field.Field>
                  <field.Content>
                    <field.Input
                      name="email"
                      type="email"
                      autoComplete="username"
                      placeholder={word("auth.form.placeholder.email")}
                    />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>
            <form.AppField name="password">
              {(field) => (
                <field.Field>
                  <field.Content>
                    <field.Password
                      inputProps={{
                        name: "password",
                        autoComplete: "current-password",
                        placeholder: word("auth.signup.form.placeholder.mdp"),
                      }}
                    />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>

            <div className="flex justify-end">
              <Button variant="underline" onClick={() => void navigateToSendCode()}>
                <Text className="font-semibold">{word("auth.signup.form.reset_password")}</Text>
              </Button>
            </div>
          </div>

          <form.Subscribe selector={(state) => [state.isSubmitting]}>
            {([isSubmitting]) => (
              <AuthSubmitButton loading={isSubmitting} buttonLabel={word("auth.login.form.button.login")} />
            )}
          </form.Subscribe>

          <div className="flex flex-1 items-center justify-center gap-1 text-center">
            <Text className="text-textDefault-placeholder">{word("auth.login.form.not_account")}</Text>

            <Button
              variant="underline"
              nativeButton={false}
              render={
                <Link to={Path.SignUp}>
                  <Text className="font-semibold">{word("auth.login.form.create_account")}</Text>
                </Link>
              }
            />
          </div>
        </AuthContent>
      </AuthForm>
    </AuthPage>
  )
}
